import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import cn from 'classnames'
import {
   Button,
   ContentCard,
   Grid,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import {
   ArrowLeftMdIcon,
   CheckCircle02Icon,
   ChevronRightIcon,
   VolumeMaxIcon,
} from '@forwardflow/icons'
import { useGetVoices } from '@forwardflow/queries/campaign.queries'
import { useOnboardingStoreV2 } from '@forwardflow/store'
import VoiceAvatar1 from '@forwardflow/assets/onboarding/voice1.png'
import VoiceAvatar2 from '@forwardflow/assets/onboarding/voice2.png'
import { DEV_DUMMY, useDevTools } from '../dev/devTools'
import { FALLBACK_VOICES, TVoiceOption } from './voice/voices'

const AVATARS = [VoiceAvatar1, VoiceAvatar2]

/**
 * Step 6: the voice, the one input onboarding insists on.
 *
 * Two voices from the curated onboarding list, the first pre-selected; Play
 * on each; the assistant's name pre-filled from the voice and editable. No
 * description, avatar or accent fields: the assistant record takes the
 * voice's own values when the campaign launches.
 */
export const Voice = () => {
   const {
      getNextStep,
      getPreviousStep,
      setCampaignConfiguration,
      onboardingData,
   } = useOnboardingStoreV2()
   const { data: voices } = useGetVoices()

   const options: TVoiceOption[] = useMemo(() => {
      if (!voices?.length) return FALLBACK_VOICES
      return voices.slice(0, 2)
   }, [voices])
   const [showAll, setShowAll] = useState(false)
   const shown = showAll && voices?.length ? voices : options

   const current = onboardingData.campaignConfiguration?.ai_assistants?.[0]
   const selectedId = current?.voice_id || options[0]?.voiceId
   const selected = shown.find((v) => v.voiceId === selectedId) || options[0]

   const choose = (voice: TVoiceOption, keepName = false) =>
      setCampaignConfiguration({
         ai_assistants: [
            {
               id: 1,
               voice_id: voice.voiceId,
               assistant_name:
                  keepName && current?.assistant_name ? current.assistant_name : voice.name,
            },
         ],
      })

   // Pre-select the first voice once, so doing nothing still picks one.
   useEffect(() => {
      if (!current?.voice_id && options[0]) choose(options[0])
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [options])

   /* ---------- Preview ---------- */

   const [playingId, setPlayingId] = useState<string | null>(null)
   const audioRef = useRef<HTMLAudioElement | null>(null)
   const stop = () => {
      if (audioRef.current) {
         audioRef.current.pause()
         audioRef.current.currentTime = 0
         audioRef.current = null
      }
      setPlayingId(null)
   }
   useEffect(() => stop, [])

   const play = (voice: TVoiceOption, e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (playingId === voice.voiceId) {
         stop()
         return
      }
      stop()
      if (!voice.previewUrl) return
      const audio = new Audio(voice.previewUrl)
      audioRef.current = audio
      setPlayingId(voice.voiceId)
      audio.onended = () => setPlayingId(null)
      audio.onerror = () => setPlayingId(null)
      void audio.play().catch(() => setPlayingId(null))
   }

   const name = current?.assistant_name ?? selected?.name ?? ''
   const setName = (value: string) => {
      if (!selected) return
      setCampaignConfiguration({
         ai_assistants: [{ id: 1, voice_id: selected.voiceId, assistant_name: value }],
      })
   }

   useDevTools({
      fill: () => {
         if (options[0]) choose(options[0])
         setName(DEV_DUMMY.assistantName)
      },
   })

   const canContinue = Boolean(selected) && name.trim().length > 0

   return (
      <Stack gap={24}>
         <ContentCard
            className="space-y-5"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <Stack gap={4}>
               <p className="text-white font-semibold text-lg">
                  Which voice should your leads hear?
               </p>
               <p className="text-[#99A1AF] text-sm font-medium max-w-[640px]">
                  Press play to hear each one. This is the voice that calls,
                  qualifies and books for you. You can change it any time.
               </p>
            </Stack>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" role="radiogroup">
               {shown.map((voice, index) => {
                  const isSelected = selected?.voiceId === voice.voiceId
                  const isPlaying = playingId === voice.voiceId
                  return (
                     <motion.button
                        key={voice.voiceId}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        data-testid={`voice-${index}`}
                        className={cn(
                           'rounded-[16px] border text-left p-5 flex flex-col items-center gap-3 transition-all',
                           isSelected
                              ? 'border-[#9F7BFF] bg-[#9F7BFF]/10'
                              : 'border-white/15 bg-white/[.02] hover:border-white/30'
                        )}
                        onClick={() => choose(voice, true)}
                     >
                        <div className="relative">
                           <img
                              src={AVATARS[index % AVATARS.length]}
                              alt={voice.name}
                              className={cn(
                                 'w-36 relative z-10 transition-all duration-300',
                                 isPlaying && 'scale-105'
                              )}
                           />
                           {isPlaying && (
                              <>
                                 <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-2xl opacity-60 animate-pulse pointer-events-none" />
                                 <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-3xl opacity-40 animate-ping pointer-events-none" />
                              </>
                           )}
                        </div>
                        <div className="text-center">
                           <p className="text-white font-semibold">{voice.name}</p>
                           {voice.description && (
                              <p className="text-[#99A1AF] text-xs mt-0.5">{voice.description}</p>
                           )}
                        </div>
                        <div className="flex items-center gap-3">
                           <span
                              role="button"
                              tabIndex={0}
                              className={cn(
                                 'rounded-[12px] flex items-center gap-2 border px-4 py-2 text-sm text-white transition-all cursor-pointer hover:bg-white/[.08]',
                                 isPlaying
                                    ? 'border-[#9F7BFF] bg-[#9F7BFF]/20'
                                    : 'border-white/15 bg-white/[.02]',
                                 !voice.previewUrl && 'opacity-40 cursor-not-allowed'
                              )}
                              title={voice.previewUrl ? undefined : 'No preview for this voice'}
                              onClick={(e) => play(voice, e)}
                              onKeyDown={(e) => {
                                 if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    play(voice)
                                 }
                              }}
                           >
                              <VolumeMaxIcon width={16} />
                              {isPlaying ? 'Stop' : 'Play'}
                           </span>
                           <span
                              className={cn(
                                 'flex items-center gap-1 text-sm font-medium transition-opacity',
                                 isSelected ? 'text-[#A684FF] opacity-100' : 'opacity-0'
                              )}
                           >
                              <CheckCircle02Icon width={16} height={16} /> Selected
                           </span>
                        </div>
                     </motion.button>
                  )
               })}
            </div>

            {(voices?.length ?? 0) > 2 && (
               <button
                  type="button"
                  className="text-[#A684FF] text-sm font-medium hover:underline"
                  onClick={() => setShowAll((v) => !v)}
               >
                  {showAll ? 'Fewer voices' : `More voices (${voices!.length - 2})`}
               </button>
            )}

            <TextInput
               label="Your assistant's name"
               description="How it introduces itself on calls"
               value={name}
               onChange={(e) => setName(e.currentTarget.value)}
               maxLength={40}
               data-testid="assistant-name"
            />
         </ContentCard>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="space-y-2"
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => {
                        stop()
                        getPreviousStep()
                     }}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={() => {
                        stop()
                        getNextStep()
                     }}
                     disabled={!canContinue}
                  >
                     {canContinue
                        ? `Use ${selected?.name}'s voice`
                        : 'Give your assistant a name'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
            <p className="text-center text-xs text-white/40">
               Last step: a quick summary, then we start calling.
            </p>
         </motion.div>
      </Stack>
   )
}
